import { FormEvent, useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { Loader2 } from 'lucide-react';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  createOpenCheckoutSession,
  fetchOpenCheckoutWorkspace,
  OpenCheckoutWorkspaceResponse,
} from '@/lib/lendliftedService';
import { logFirebaseEvent } from '@/lib/tracking';

const formatCurrency = (amount: number, currency = 'USD') =>
  new Intl.NumberFormat('en-US', { style: 'currency', currency: currency.toUpperCase() }).format(amount);

export default function OpenCheckoutPage() {
  const { slug } = useParams<{ slug: string }>();
  const [workspace, setWorkspace] = useState<OpenCheckoutWorkspaceResponse | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [amount, setAmount] = useState('');
  const [description, setDescription] = useState('');
  const [email, setEmail] = useState('');
  const [customerName, setCustomerName] = useState('');

  useEffect(() => {
    if (!slug) {
      setError('This seller page is invalid.');
      setIsLoading(false);
      return;
    }
    let cancelled = false;
    const run = async () => {
      setIsLoading(true);
      setError(null);
      try {
        const data = await fetchOpenCheckoutWorkspace(slug);
        if (cancelled) return;
        setWorkspace(data);
        logFirebaseEvent('open_link_viewed', {
          workspace_id: data.workspaceId,
          slug: data.slug,
        });
      } catch (e: any) {
        if (!cancelled) setError(e?.message || 'Could not load this seller page.');
      } finally {
        if (!cancelled) setIsLoading(false);
      }
    };
    run();
    return () => {
      cancelled = true;
    };
  }, [slug]);

  const handleSubmit = async (event: FormEvent) => {
    event.preventDefault();
    if (!workspace || !slug) return;

    const parsedAmount = Number(amount);
    if (!Number.isFinite(parsedAmount)) {
      toast.error('Enter a valid amount.');
      return;
    }
    if (parsedAmount < workspace.minAmount || parsedAmount > workspace.maxAmount) {
      toast.error(`Amount must be between ${formatCurrency(workspace.minAmount)} and ${formatCurrency(workspace.maxAmount)}.`);
      return;
    }
    if (!description.trim()) {
      toast.error('Please add a short description for the purchase.');
      return;
    }

    setIsSubmitting(true);
    try {
      logFirebaseEvent('open_link_submitted', {
        workspace_id: workspace.workspaceId,
        slug,
        amount: Number(parsedAmount.toFixed(2)),
        has_email: email.trim() ? 1 : 0,
      });
      const result = await createOpenCheckoutSession({
        slug,
        amount: parsedAmount,
        description: description.trim(),
        email: email.trim() || undefined,
        customerName: customerName.trim() || undefined,
      });
      window.location.assign(result.checkoutUrl);
    } catch (e: any) {
      toast.error(e?.message || 'Could not start checkout.');
      setIsSubmitting(false);
    }
  };

  if (isLoading) {
    return (
      <div className="grid min-h-screen place-content-center">
        <Loader2 className="h-8 w-8 animate-spin text-slate-700" />
      </div>
    );
  }

  return (
    <section className="min-h-screen bg-gradient-to-b from-white via-slate-50 to-slate-100 px-4 py-10 sm:px-6">
      <div className="mx-auto max-w-xl rounded-3xl border border-slate-900/10 bg-white/95 p-8 shadow-sm">
        <p className="text-xs font-semibold uppercase tracking-[0.18em] text-slate-500">Flexible Payments</p>
        <h1 className="mt-2 font-[Sora] text-3xl text-slate-900">
          {workspace ? `Pay ${workspace.businessName}` : 'Pay with financing'}
        </h1>
        <p className="mt-3 text-sm text-slate-600">
          Enter the amount and details below to continue to secure checkout and view available pay-over-time options.
        </p>

        {error ? (
          <p className="mt-5 rounded-xl border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">{error}</p>
        ) : (
          <form onSubmit={handleSubmit} className="mt-6 space-y-4">
            <div className="space-y-2">
              <Label htmlFor="open-amount">Amount (USD)</Label>
              <Input
                id="open-amount"
                inputMode="decimal"
                placeholder="e.g. 1200"
                value={amount}
                onChange={(e) => setAmount(e.target.value)}
                required
              />
              {workspace && (
                <p className="text-xs text-slate-500">
                  Allowed range: {formatCurrency(workspace.minAmount)} to {formatCurrency(workspace.maxAmount)}
                </p>
              )}
            </div>

            <div className="space-y-2">
              <Label htmlFor="open-description">What is this for?</Label>
              <Input
                id="open-description"
                placeholder="Tattoo session, furniture deposit, repair project..."
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                required
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="open-customer-name">Your name (optional)</Label>
              <Input
                id="open-customer-name"
                placeholder="Jane Doe"
                value={customerName}
                onChange={(e) => setCustomerName(e.target.value)}
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="open-email">Email (optional)</Label>
              <Input
                id="open-email"
                type="email"
                placeholder="jane@example.com"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
              />
            </div>

            <Button type="submit" className="w-full" disabled={isSubmitting || !!error}>
              {isSubmitting ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : null}
              Continue to secure checkout
            </Button>
          </form>
        )}
      </div>
    </section>
  );
}
